/**
 * Centralized Moving Average Calculator Selector
 *
 * Returns the appropriate MA calculator based on type string.
 * Supports: SMA, EMA, WMA, TMA, DEMA, VIDYA, TSMA, VMA, SMMA
 *
 * @param {string} maType - The moving average type (case-insensitive)
 * @returns {Function} The MA calculator factory function
 *
 * @example
 * import { getMACalculator } from './utils';
 * const maCalculator = getMACalculator('ema');
 * const ma = maCalculator().options({ windowSize: 20, sourcePath: 'close' });
 * const values = ma(data);
 */

import sma from "../calculator/sma";
import ema from "../calculator/ema";
import wma from "../calculator/wma";
import hma from "../calculator/hma";
import tma from "../calculator/tma";
import dema from "../calculator/dema";
import tema from "../calculator/tema";
import vidya from "../calculator/vidya";
import tsma from "../calculator/tsma";
import vma from "../calculator/vma";
import smma from "../calculator/smma";

// Map of MA type strings to calculator factory functions
const MA_TYPE_MAP = {
  sma,
  ema,
  wma,
  hma,
  tma,
  dema,
  tema,
  vidya,
  tsma,
  vma,
  smma,
};

/**
 * Available Moving Average types with labels for UI
 * This is the single source of truth for MA types
 */
export const MA_TYPES = [
  { value: "sma", label: "Simples (SMA)" },
  { value: "ema", label: "Exponencial (EMA)" },
  { value: "wma", label: "Ponderada (WMA)" },
  { value: "hma", label: "Hull (HMA)" },
  { value: "tma", label: "Triangular (TMA)" },
  { value: "dema", label: "Dupla Exponencial (DEMA)" },
  { value: "tema", label: "Tripla Exponencial (TEMA)" },
  { value: "vidya", label: "VIDYA" },
  { value: "tsma", label: "Série Temporal (TSMA)" },
  { value: "vma", label: "Variável (VMA)" },
  { value: "smma", label: "Suavizada (SMMA)" },
];

/**
 * Get the display label for a Moving Average type (for tooltips)
 * @param {string} maType - Moving average type (e.g., 'sma', 'ema', 'wma')
 * @returns {string} Short label (e.g., 'SMA', 'EMA', 'WMA')
 *
 * @example
 * getMALabel('sma') // Returns 'SMA'
 * getMALabel('ema') // Returns 'EMA'
 * getMALabel('vidya') // Returns 'VIDYA' (when added)
 */
export function getMALabel(maType) {
  if (!maType) return "SMA";

  const normalizedType = maType.toLowerCase().trim();

  // Find the label from MA_TYPES array
  const maTypeConfig = MA_TYPES.find((mt) => mt.value === normalizedType);

  if (maTypeConfig) {
    // Extract short label (e.g., "Simples (SMA)" -> "SMA")
    const match = maTypeConfig.label.match(/\(([^)]+)\)/);
    return match ? match[1] : normalizedType.toUpperCase();
  }

  // If not found, return uppercase (for unknown types)
  return normalizedType.toUpperCase();
}

/**
 * Get the appropriate Moving Average calculator for the given type
 * @param {string} maType - Moving average type (e.g., 'sma', 'ema', 'wma', 'tma', 'dema', 'vidya', 'tsma', 'vma', 'smma')
 * @returns {Function} MA calculator factory function
 */
export default function getMACalculator(maType = "sma") {
  const normalizedType = (maType || "sma").toLowerCase().trim();
  return MA_TYPE_MAP[normalizedType] || MA_TYPE_MAP.sma;
}
